import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import supabase from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import {
  EDITABLE_CONTENT_REGISTRY,
  getEditableRegistryId,
  getEditableRegistryItem,
} from '../../data/editableContentRegistry';
import * as FiIcons from 'react-icons/fi';

const {
  FiSave,
  FiArrowLeft,
  FiAlertCircle,
  FiEdit,
  FiTrash2,
  FiPlus,
  FiX,
  FiSearch,
  FiCheckCircle,
  FiAlertTriangle,
  FiMapPin,
  FiRefreshCw,
} = FiIcons;

const emptyContent = {
  page: '',
  section: '',
  content_key: '',
  content: '',
};

const EditableContentManager = () => {
  const navigate = useNavigate();
  const [contentItems, setContentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyContent);
  const [filterPage, setFilterPage] = useState('all');
  const [filterState, setFilterState] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchContent = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('editable_content_aox1234')
        .select('id,page,section,key,content,content_html,created_at,updated_at')
        .order('page')
        .order('section')
        .order('key');

      if (fetchError) throw fetchError;

      setContentItems((data || []).map((item) => ({
        ...item,
        content_key: item.key,
      })));
    } catch (err) {
      console.error('Error fetching editable content:', err);
      setError(err.message || 'Αδυναμία φόρτωσης περιεχομένου');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const pages = useMemo(
    () => [...new Set(EDITABLE_CONTENT_REGISTRY.map((item) => item.page))],
    []
  );

  const registeredIds = useMemo(
    () => new Set(EDITABLE_CONTENT_REGISTRY.map((item) => getEditableRegistryId(item.page, item.section, item.key))),
    []
  );

  const existingIds = useMemo(
    () => new Set(contentItems.map((item) => getEditableRegistryId(item.page, item.section, item.content_key))),
    [contentItems]
  );

  const missingRegistryItems = useMemo(
    () => EDITABLE_CONTENT_REGISTRY.filter(
      (item) => !existingIds.has(getEditableRegistryId(item.page, item.section, item.key))
    ),
    [existingIds]
  );

  const displayRows = useMemo(() => {
    const databaseRows = contentItems.map((item) => {
      const registry = getEditableRegistryItem(item.page, item.section, item.content_key);
      return {
        ...item,
        registry,
        connectionState: registry ? 'connected' : 'legacy',
      };
    });

    const missingRows = missingRegistryItems.map((item) => ({
      id: `missing:${getEditableRegistryId(item.page, item.section, item.key)}`,
      page: item.page,
      section: item.section,
      content_key: item.key,
      content: '',
      content_html: null,
      registry: item,
      connectionState: 'missing',
      isVirtual: true,
    }));

    return [...databaseRows, ...missingRows];
  }, [contentItems, missingRegistryItems]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return displayRows.filter((item) => {
      if (filterPage !== 'all' && item.page !== filterPage) return false;
      if (filterState !== 'all' && item.connectionState !== filterState) return false;

      if (!term) return true;
      const haystack = [
        item.page,
        item.section,
        item.content_key,
        item.content,
        item.registry?.label,
        item.registry?.location,
      ].filter(Boolean).join(' ').toLowerCase();

      return haystack.includes(term);
    });
  }, [displayRows, filterPage, filterState, searchTerm]);

  const resetForm = () => {
    setFormData(emptyContent);
    setEditingId(null);
    setShowForm(false);
  };

  const openEdit = (item) => {
    setFormData({
      page: item.page,
      section: item.section,
      content_key: item.content_key,
      content: item.content || item.content_html || '',
    });
    setEditingId(item.isVirtual ? null : item.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage({ type: '', text: '' });

    try {
      const page = formData.page.trim();
      const section = formData.section.trim();
      const key = formData.content_key.trim();
      if (!page || !section || !key) throw new Error('Page, section και key είναι υποχρεωτικά.');

      const registry = getEditableRegistryItem(page, section, key);
      if (!registry) {
        throw new Error('Το πεδίο δεν είναι καταχωρημένο στο ενεργό Content Registry.');
      }

      const payload = {
        page,
        section,
        key,
        content: formData.content,
        content_html: null,
        updated_at: new Date().toISOString(),
      };

      let saveError;
      if (editingId) {
        ({ error: saveError } = await supabase
          .from('editable_content_aox1234')
          .update(payload)
          .eq('id', editingId));
      } else {
        ({ error: saveError } = await supabase
          .from('editable_content_aox1234')
          .upsert([payload], { onConflict: 'page,section,key' }));
      }

      if (saveError) throw saveError;

      setMessage({ type: 'success', text: 'Το περιεχόμενο αποθηκεύτηκε.' });
      resetForm();
      await fetchContent();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Αποτυχία αποθήκευσης.' });
    }
  };

  const handleDelete = async (item) => {
    if (!item.id || item.isVirtual) return;
    if (!window.confirm('Να διαγραφεί αυτό το περιεχόμενο;')) return;

    try {
      const { error: deleteError } = await supabase
        .from('editable_content_aox1234')
        .delete()
        .eq('id', item.id);

      if (deleteError) throw deleteError;
      setMessage({ type: 'success', text: 'Το περιεχόμενο διαγράφηκε.' });
      await fetchContent();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Αποτυχία διαγραφής.' });
    }
  };

  const connectedCount = contentItems.filter((item) =>
    registeredIds.has(getEditableRegistryId(item.page, item.section, item.content_key))
  ).length;
  const legacyCount = contentItems.length - connectedCount;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/admin/dashboard')} className="p-2 rounded-md text-gray-500 hover:bg-gray-100">
                <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Διαχείριση Περιεχομένου</h1>
                <p className="text-xs text-gray-500">Βλέπετε ακριβώς πού εμφανίζεται κάθε πεδίο στο δημόσιο site.</p>
              </div>
            </div>
            <button onClick={fetchContent} className="inline-flex items-center px-3 py-2 border rounded-md text-sm bg-white hover:bg-gray-50">
              <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />Ανανέωση
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message.text && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className={`mb-6 p-4 rounded-md flex items-center ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 mr-2" />{message.text}
          </motion.div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4"><div className="text-xs text-gray-500">Connected</div><div className="text-2xl font-bold text-green-700">{connectedCount}</div></div>
          <div className="bg-white rounded-lg shadow-sm p-4"><div className="text-xs text-gray-500">Missing records</div><div className="text-2xl font-bold text-amber-700">{missingRegistryItems.length}</div></div>
          <div className="bg-white rounded-lg shadow-sm p-4"><div className="text-xs text-gray-500">Legacy / Unused</div><div className="text-2xl font-bold text-red-700">{legacyCount}</div></div>
          <div className="bg-white rounded-lg shadow-sm p-4"><div className="text-xs text-gray-500">Active registry</div><div className="text-2xl font-bold text-primary-700">{EDITABLE_CONTENT_REGISTRY.length}</div></div>
        </div>

        {showForm && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-md p-6 mb-8">
            <div className="flex justify-between gap-4 mb-5">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Επεξεργασία Περιεχομένου</h2>
                <p className="text-sm text-gray-500 mt-1">{getEditableRegistryItem(formData.page, formData.section, formData.content_key)?.location}</p>
              </div>
              <button type="button" onClick={resetForm} className="p-2 rounded-md hover:bg-gray-100"><SafeIcon icon={FiX} className="w-5 h-5" /></button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input readOnly value={formData.page} className="rounded-md border bg-gray-50 px-3 py-2 text-sm" />
                <input readOnly value={formData.section} className="rounded-md border bg-gray-50 px-3 py-2 text-sm" />
                <input readOnly value={formData.content_key} className="rounded-md border bg-gray-50 px-3 py-2 text-sm" />
              </div>

              <div className="bg-white">
                <ReactQuill
                  theme="snow"
                  value={formData.content}
                  onChange={(value) => setFormData((prev) => ({ ...prev, content: value }))}
                  modules={{ toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']] }}
                />
              </div>

              <div className="flex justify-end gap-3">
                <button type="button" onClick={resetForm} className="px-4 py-2 border rounded-md bg-white">Ακύρωση</button>
                <button type="submit" className="inline-flex items-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700"><SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />Αποθήκευση</button>
              </div>
            </form>
          </motion.div>
        )}

        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <select value={filterPage} onChange={(e) => setFilterPage(e.target.value)} className="rounded-md border px-3 py-2 text-sm">
              <option value="all">Όλες οι σελίδες</option>
              {pages.map((page) => <option key={page} value={page}>{page}</option>)}
            </select>
            <select value={filterState} onChange={(e) => setFilterState(e.target.value)} className="rounded-md border px-3 py-2 text-sm">
              <option value="all">Όλες οι καταστάσεις</option>
              <option value="connected">Connected</option>
              <option value="missing">Missing record</option>
              <option value="legacy">Legacy / Not connected</option>
            </select>
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Αναζήτηση..." className="w-full rounded-md border pl-9 pr-3 py-2 text-sm" />
            </div>
          </div>
        </div>

        {error && <div className="mb-6 rounded-md bg-red-50 p-4 text-red-700">{error}</div>}

        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-gray-500">Φόρτωση...</div>
          ) : filteredRows.length === 0 ? (
            <div className="p-10 text-center text-gray-500">Δεν βρέθηκαν πεδία.</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredRows.map((item) => {
                const connected = item.connectionState === 'connected';
                const missing = item.connectionState === 'missing';
                return (
                  <div key={item.id} className={`p-5 ${item.connectionState === 'legacy' ? 'bg-red-50/30' : ''}`}>
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="text-xs font-semibold rounded-full bg-gray-100 px-2.5 py-1">{item.page}</span>
                          <span className="text-xs font-semibold rounded-full bg-blue-50 text-blue-700 px-2.5 py-1">{item.section}</span>
                          {connected && <span className="inline-flex items-center text-xs font-semibold rounded-full bg-green-100 text-green-800 px-2.5 py-1"><SafeIcon icon={FiCheckCircle} className="w-3.5 h-3.5 mr-1" />Connected</span>}
                          {missing && <span className="inline-flex items-center text-xs font-semibold rounded-full bg-amber-100 text-amber-800 px-2.5 py-1"><SafeIcon icon={FiAlertTriangle} className="w-3.5 h-3.5 mr-1" />Missing record</span>}
                          {item.connectionState === 'legacy' && <span className="inline-flex items-center text-xs font-semibold rounded-full bg-red-100 text-red-800 px-2.5 py-1"><SafeIcon icon={FiAlertTriangle} className="w-3.5 h-3.5 mr-1" />Not connected</span>}
                        </div>

                        <h3 className="font-semibold text-gray-900">{item.registry?.label || `${item.section} / ${item.content_key}`}</h3>
                        <div className="mt-1 text-xs text-gray-500 font-mono">{item.page}.{item.section}.{item.content_key}</div>
                        {item.registry?.location && <div className="mt-2 inline-flex items-center text-sm text-gray-600"><SafeIcon icon={FiMapPin} className="w-4 h-4 mr-1.5 text-primary-600" />{item.registry.location}</div>}
                        <div className="mt-3 text-sm text-gray-700 line-clamp-2" dangerouslySetInnerHTML={{ __html: item.content || '<em>Δεν έχει αποθηκευμένο περιεχόμενο ακόμη.</em>' }} />
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {item.connectionState !== 'legacy' && (
                          <button onClick={() => openEdit(item)} className="inline-flex items-center px-3 py-2 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100">
                            <SafeIcon icon={missing ? FiPlus : FiEdit} className="w-4 h-4 mr-2" />{missing ? 'Δημιουργία' : 'Επεξεργασία'}
                          </button>
                        )}
                        {item.connectionState === 'legacy' && !item.isVirtual && (
                          <button onClick={() => handleDelete(item)} className="inline-flex items-center px-3 py-2 rounded-md bg-red-50 text-red-700 hover:bg-red-100">
                            <SafeIcon icon={FiTrash2} className="w-4 h-4 mr-2" />Διαγραφή legacy
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditableContentManager;
